import { useCallback, useEffect, useMemo, useState } from 'react'
import { API } from './api'
import { adminRequest } from './EshopCategories'
import { BranchesEditor } from './StockAdmin'

type Customer = {
  id: string
  email: string
  name: string
  phone: string | null
  emailVerified: boolean
  isCompany: boolean
  companyName: string | null
  companyIco: string | null
  companyDic: string | null
  companyIcDph: string | null
  billingAddress: string | null
  discountPercent: number
  pricingMode: 'DISCOUNT' | 'B2B'
  active: boolean
  adminNote: string | null
  marketingConsent: boolean
  createdAt: string
  lastLoginAt: string | null
  orderCount: number
  spentCents: number
}
type Draft = { discountPercent: string; pricingMode: 'DISCOUNT' | 'B2B'; active: boolean; adminNote: string; isCompany: boolean; companyName: string; companyIco: string; companyDic: string; companyIcDph: string; billingAddress: string }

const euro = (cents: number) => (cents / 100).toLocaleString('sk-SK', { style: 'currency', currency: 'EUR' })
const date = (value: string | null) => value ? new Date(value).toLocaleDateString('sk-SK') : '—'

export default function CustomersAdmin() {
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loyalty, setLoyalty] = useState('0')
  const [savedLoyalty, setSavedLoyalty] = useState(0)
  const [emailConfigured, setEmailConfigured] = useState<boolean | null>(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'company' | 'unverified' | 'blocked'>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [resetLink, setResetLink] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const [data, settings] = await Promise.all([adminRequest('/api/admin/customers'), adminRequest('/api/admin/shop-settings')])
      setCustomers(Array.isArray(data?.customers) ? data.customers : [])
      setSavedLoyalty(Number(settings?.loyaltyDiscountPercent ?? 0))
      setLoyalty(String(settings?.loyaltyDiscountPercent ?? 0))
      setEmailConfigured(Boolean(settings?.emailConfigured))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Zákazníkov sa nepodarilo načítať.')
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { void load() }, [load])

  const selected = customers.find(c => c.id === selectedId) ?? null
  useEffect(() => {
    setResetLink('')
    setDraft(selected ? {
      discountPercent: String(selected.discountPercent), pricingMode: selected.pricingMode ?? 'DISCOUNT', active: selected.active, adminNote: selected.adminNote ?? '',
      isCompany: selected.isCompany, companyName: selected.companyName ?? '', companyIco: selected.companyIco ?? '',
      companyDic: selected.companyDic ?? '', companyIcDph: selected.companyIcDph ?? '', billingAddress: selected.billingAddress ?? '',
    } : null)
    // Formulár sa nastaví iba pri výbere iného zákazníka.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId])

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('sk')
    return customers.filter(c =>
      (filter === 'all' || (filter === 'company' && c.isCompany) || (filter === 'unverified' && !c.emailVerified) || (filter === 'blocked' && !c.active))
      && (!q || `${c.name} ${c.email} ${c.companyName ?? ''} ${c.companyIco ?? ''} ${c.phone ?? ''}`.toLocaleLowerCase('sk').includes(q)))
  }, [customers, search, filter])

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true); setError(''); setNotice('')
    try { await action(); setNotice(success) } catch (e) { setError(e instanceof Error ? e.message : 'Akcia sa nepodarila.') } finally { setBusy(false) }
  }

  const newsletterCount = customers.filter(c => c.marketingConsent).length
  async function exportNewsletter() {
    setError('')
    try {
      const response = await fetch(`${API}/api/admin/customers/newsletter-export`, { credentials: 'include' })
      if (!response.ok) throw new Error(`Export sa nepodaril (HTTP ${response.status}).`)
      const blob = await response.blob()
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'newsletter-odber.csv'
      link.click()
      setTimeout(() => URL.revokeObjectURL(link.href), 1000)
    } catch (e) { setError(e instanceof Error ? e.message : 'Export sa nepodaril.') }
  }

  async function syncNewsletterNow() {
    setBusy(true); setError(''); setNotice('')
    try {
      const data = await adminRequest('/api/admin/customers/newsletter-sync', { method: 'POST' })
      setNotice(`Do Ecomailu sme poslali ${data.synced} e-mailov.`)
    } catch (e) { setError(e instanceof Error ? e.message : 'Synchronizácia sa nepodarila.') } finally { setBusy(false) }
  }

  const saveLoyalty = () => run(async () => {
    const value = Math.round(Number(loyalty))
    const data = await adminRequest('/api/admin/shop-settings', { method: 'PUT', body: JSON.stringify({ loyaltyDiscountPercent: value }) })
    setSavedLoyalty(data.loyaltyDiscountPercent); setLoyalty(String(data.loyaltyDiscountPercent))
  }, 'Vernostná zľava je uložená.')

  const saveCustomer = (extra: Record<string, unknown> = {}) => selected && draft && run(async () => {
    await adminRequest(`/api/admin/customers/${encodeURIComponent(selected.id)}`, {
      method: 'PATCH',
      body: JSON.stringify({
        discountPercent: Math.max(0, Math.min(90, Math.round(Number(draft.discountPercent) || 0))),
        pricingMode: draft.pricingMode,
        active: draft.active, adminNote: draft.adminNote.trim() || null,
        isCompany: draft.isCompany, companyName: draft.companyName.trim() || null, companyIco: draft.companyIco.trim() || null,
        companyDic: draft.companyDic.trim() || null, companyIcDph: draft.companyIcDph.trim() || null, billingAddress: draft.billingAddress.trim() || null,
        ...extra,
      }),
    })
    await load()
  }, 'Zákazník je uložený.')

  const createResetLink = () => selected && run(async () => {
    const data = await adminRequest(`/api/admin/customers/${encodeURIComponent(selected.id)}/password-reset-link`, { method: 'POST' })
    setResetLink(data.link)
    if (!data.emailSent) setNotice('Odkaz je pripravený – e-mail sa neposlal, skopíruj ho a pošli zákazníkovi.')
  }, 'Odkaz na nové heslo sme poslali zákazníkovi e-mailom. Platí 24 hodín.')

  return <div className="panel customers-admin">
    <div className="panel-heading"><div><h2>Zákazníci e-shopu</h2><p>Registrovaní zákazníci, firemné údaje a zľavy.</p></div>
      <div className="pa-head-actions">
        <button className="text-btn" onClick={() => void syncNewsletterNow()} disabled={busy}>⟳ Synchronizovať s Ecomailom</button>
        <button className="text-btn" onClick={() => void exportNewsletter()}>⬇ Newsletter – export CSV ({newsletterCount}+)</button>
        <button className="text-btn" onClick={() => void load()} disabled={loading}>↻ Obnoviť</button>
      </div>
    </div>
    <p className="hint">Zoznam e-mailov so súhlasom „Chcem dostávať novinky a akcie“ (od registrovaných zákazníkov aj z objednávok bez účtu) sa do Ecomailu posiela automaticky pri každom novom súhlase. Tlačidlo „Synchronizovať“ pošle celý aktuálny zoznam naraz – napríklad pri prvom nastavení. CSV export je záloha, keby ste chceli zoznam nahrať inde ručne.</p>

    <section className="customers-settings">
      <label>Vernostná zľava pre registrovaných (%)
        <input type="number" min="0" max="90" step="1" value={loyalty} onChange={e => setLoyalty(e.target.value)} />
      </label>
      <button className="primary-btn" disabled={busy || Number(loyalty) === savedLoyalty || loyalty === ''} onClick={() => void saveLoyalty()}>Uložiť</button>
      <p className="hint">Platí pre každého registrovaného zákazníka s overeným e-mailom. Individuálna zľava (napr. pre firmy) sa nastavuje pri zákazníkovi – použije sa vyššia z nich. Zľava sa počíta z ceny produktov, nie z dopravy. 0 = bez vernostnej zľavy.</p>
      {emailConfigured === false && <p className="eshop-cat-warning">E-maily zatiaľ nie sú nastavené (SMTP v súbore .env). Zákazníci nedostanú overenie e-mailu ani odkaz na nové heslo – odkaz môžeš vytvoriť pri zákazníkovi a poslať ho sám.</p>}
    </section>

    {error && <p className="error-message">{error}</p>}
    {notice && <p className="eshop-cat-notice" role="status">{notice}</p>}

    <div className="sk-filter-row">
      <label className="pa-search"><span aria-hidden="true">⌕</span><input placeholder="Hľadať meno, e-mail, firmu alebo IČO…" value={search} onChange={e => setSearch(e.target.value)} /></label>
      <select value={filter} onChange={e => setFilter(e.target.value as typeof filter)}>
        <option value="all">Všetci ({customers.length})</option>
        <option value="company">Firmy</option>
        <option value="unverified">Neoverený e-mail</option>
        <option value="blocked">Zablokovaní</option>
      </select>
    </div>

    {loading ? <p className="empty-message">Načítavam zákazníkov…</p> : !customers.length ? <p className="empty-message">Zatiaľ sa nikto nezaregistroval.</p> : <div className="cat-layout customers-layout">
      <div className="pa-table-wrap"><table className="pa-table sk-table">
        <thead><tr><th>Názov</th><th>Telefón</th><th>E-mail</th><th>Firma</th><th>Objednávky</th><th>Zľava</th><th>Registrácia</th></tr></thead>
        <tbody>{filtered.map(c => <tr key={c.id} className={`${selectedId === c.id ? 'is-selected' : ''} ${c.active ? '' : 'is-inactive'}`} onClick={() => setSelectedId(c.id)}>
          <td><strong>{c.name}</strong><small className="sk-sub">{!c.emailVerified && 'neoverený e-mail'}{!c.emailVerified && !c.active ? ' · ' : ''}{!c.active && 'zablokovaný'}</small></td>
          <td>{c.phone || '—'}</td>
          <td>{c.email}</td>
          <td>{c.isCompany ? <>{c.companyName}<small className="sk-sub">IČO {c.companyIco}</small></> : '—'}</td>
          <td>{c.orderCount}<small className="sk-sub">{euro(c.spentCents)}</small></td>
          <td>{c.pricingMode === 'B2B' ? 'B2B ceny' : c.discountPercent ? `${c.discountPercent} %` : c.emailVerified && savedLoyalty ? `${savedLoyalty} % (vernostná)` : '—'}</td>
          <td>{date(c.createdAt)}</td>
        </tr>)}</tbody>
      </table></div>

      <section className="cat-detail">
        {!selected || !draft ? <div className="cat-empty"><strong>Vyber zákazníka</strong><p>Nastavíš mu individuálnu zľavu, firemné údaje alebo mu pošleš odkaz na nové heslo.</p></div> : <>
          <h3>{selected.name}</h3>
          <dl className="customer-facts">
            <dt>E-mail</dt><dd>{selected.email} {selected.emailVerified ? <span className="product-status is-active">overený</span> : <span className="product-status is-warning">neoverený</span>}</dd>
            <dt>Telefón</dt><dd>{selected.phone || '—'}</dd>
            <dt>Posledné prihlásenie</dt><dd>{date(selected.lastLoginAt)}</dd>
            <dt>Objednávky</dt><dd>{selected.orderCount} · {euro(selected.spentCents)}</dd>
          </dl>
          <div className="cat-fields">
            <label>Cenotvorba pri objednávke z administrácie<select value={draft.pricingMode} onChange={e => setDraft({ ...draft, pricingMode: e.target.value as 'DISCOUNT' | 'B2B' })}>
              <option value="DISCOUNT">Zľava z maloobchodnej ceny</option>
              <option value="B2B">B2B ceny (z karty produktu)</option>
            </select></label>
            {draft.pricingMode === 'DISCOUNT'
              ? <label>Individuálna zľava (%)<input type="number" min="0" max="90" step="1" value={draft.discountPercent} onChange={e => setDraft({ ...draft, discountPercent: e.target.value })} /></label>
              : <p className="hint cat-wide">Pri objednávke vytvorenej v administrácii pre tohto odberateľa sa použijú B2B ceny nastavené pri jednotlivých produktoch (Produkty → karta produktu → Veľkoobchodná cena B2B). Produkt bez nastavenej B2B ceny sa predá za bežnú maloobchodnú cenu.</p>}
            <label className="check-label"><input type="checkbox" checked={draft.active} onChange={e => setDraft({ ...draft, active: e.target.checked })} /> Účet je aktívny</label>
            <label className="check-label cat-wide"><input type="checkbox" checked={draft.isCompany} onChange={e => setDraft({ ...draft, isCompany: e.target.checked })} /> Firemný zákazník (B2B)</label>
            {draft.isCompany && <>
              <label>Názov firmy<input value={draft.companyName} onChange={e => setDraft({ ...draft, companyName: e.target.value })} /></label>
              <label>IČO<input value={draft.companyIco} onChange={e => setDraft({ ...draft, companyIco: e.target.value })} /></label>
              <label>DIČ<input value={draft.companyDic} onChange={e => setDraft({ ...draft, companyDic: e.target.value })} /></label>
              <label>IČ DPH<input value={draft.companyIcDph} onChange={e => setDraft({ ...draft, companyIcDph: e.target.value })} /></label>
              <label className="cat-wide">Fakturačná adresa<input value={draft.billingAddress} onChange={e => setDraft({ ...draft, billingAddress: e.target.value })} /></label>
            </>}
            <label className="cat-wide">Interná poznámka (zákazník ju nevidí)<textarea rows={2} value={draft.adminNote} onChange={e => setDraft({ ...draft, adminNote: e.target.value })} /></label>
          </div>
          <div className="customer-actions">
            <button className="primary-btn" disabled={busy} onClick={() => void saveCustomer()}>Uložiť zákazníka</button>
            {!selected.emailVerified && <button className="text-btn" disabled={busy} onClick={() => void saveCustomer({ markEmailVerified: true })}>Označiť e-mail ako overený</button>}
            <button className="text-btn" disabled={busy} onClick={() => void createResetLink()}>Odkaz na nové heslo</button>
          </div>
          {resetLink && <div className="reset-link"><span>Odkaz platí 24 hodín a dá sa použiť raz:</span><input readOnly value={resetLink} onFocus={e => e.target.select()} /><button type="button" className="text-btn" onClick={() => void navigator.clipboard?.writeText(resetLink)}>Kopírovať</button></div>}
          <BranchesEditor basePath={`/api/admin/customers/${selected.id}`} />
        </>}
      </section>
    </div>}
  </div>
}
